import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, ExternalLink, Mail, Pencil, Star, Archive, UserRound, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KINDS } from "@/lib/kinds";
import { EntityBadge, GroupChip, GroupIcon } from "@/components/common/Visuals";
import { ConfirmDialog } from "@/components/common/Dialogs";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { BackLink } from "@/components/common/Detail";
import { PersonCard } from "@/components/persons/PersonViews";
import { PersonPicker } from "@/components/persons/PersonPicker";
import { GroupFormDialog } from "@/components/groups/GroupFormDialog";
import { plural } from "@/lib/format";

function PeopleSection({ title, people, testId, canEdit, onAdd, onRemove, empty }) {
  return (
    <section className="mt-10" data-testid={testId}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-extrabold">{title}</h2>
        {canEdit && <PersonPicker label="Ajouter" onPick={onAdd} excludeIds={people.map((p) => p.id)} testId={`${testId}-add`} />}
      </div>
      {!people.length ? <EmptyState title={empty} /> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {people.map((p) => (
            <div key={p.id} className="relative">
              <PersonCard person={p} />
              {canEdit && (
                <button type="button" onClick={() => onRemove(p)} title="Retirer (la fiche n'est pas supprimée)" data-testid={`${testId}-remove-${p.id}`}
                  className="absolute bottom-3 right-3 rounded-full border border-red-200 bg-white p-1.5 text-red-600 hover:bg-red-50"><X className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function GroupDetailPage({ kind }) {
  const meta = KINDS[kind];
  const { id } = useParams();
  const { isAuthed, isAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const group = useQuery({ queryKey: ["group", kind, id, isAuthed], queryFn: async () => (await api.get(`/groups/${kind}/${id}`)).data });
  const members = useQuery({
    queryKey: ["persons", "group-members", id, isAuthed],
    queryFn: async () => (await api.get(`/persons?group=${id}&page_size=60`)).data,
  });

  if (group.isLoading) return <div className="lm-container py-10"><LoadingGrid count={3} /></div>;
  if (group.isError) {
    return <div className="lm-container py-10">{group.error.response?.status === 404
      ? <EmptyState title={`${meta.singular} introuvable`} action={<Button asChild className="rounded-full"><Link to={meta.path}>Retour</Link></Button>} />
      : <ErrorState onRetry={group.refetch} />}</div>;
  }
  const g = group.data;
  const color = g.color || "#A4247F";
  const refresh = () => ["group", "groups", "persons", "featured", "stats"].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
  const run = async (fn, message) => {
    try { await fn(); toast.success(message); refresh(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const members_ = (person, action) => run(() => api.post(`/groups/${kind}/${id}/members`, { person_ids: [person.id], action }),
    action === "add" ? `${person.full_name} ajouté(e)` : `${person.full_name} retiré(e) (fiche conservée)`);
  const animators_ = (person, action) => run(() => api.post(`/groups/${kind}/${id}/animators`, { person_ids: [person.id], action }),
    action === "add" ? "Animateur ajouté" : "Animateur retiré (fiche conservée)");
  const toggleFeatured = (v) => run(() => api.patch(`/groups/${kind}/${id}`, { featured: v }), v ? "Mis en avant" : "Retiré de la mise en avant");
  const archive = async () => {
    setDeleting(true);
    try {
      await api.delete(`/groups/${kind}/${id}`);
      toast.success(`${meta.singular} archivé(e) dans Notion`);
      refresh();
      navigate(meta.path);
    } catch (err) {
      toast.error(errorMessage(err));
      setDeleting(false);
    }
  };

  return (
    <div className="lm-container pb-20">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <BackLink to={meta.path} label={meta.label} />
        {g.parent && (
          <div className="mb-4 flex items-center gap-1.5 text-sm" data-testid="group-parent-breadcrumb">
            <Link
              to={`${KINDS[g.parent.kind]?.path || "/commissions"}/${g.parent.id}`}
              className="inline-flex items-center gap-1.5 font-semibold text-primary transition-colors hover:underline"
            >
              <ArrowRight className="h-4 w-4 rotate-180" />
              Remonter vers {g.parent.name}
            </Link>
          </div>
        )}
      </div>
      <section className="lm-card relative overflow-hidden p-6 sm:p-8 lm-fade-up" data-testid="group-detail-header"
        style={{ backgroundImage: `linear-gradient(135deg, ${color}1F 0%, transparent 55%)` }}>
        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <span className="h-20 w-20 shrink-0 rounded-3xl"><GroupIcon group={g} box className="h-9 w-9" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <EntityBadge type={kind} />
              {g.type && g.type !== "Club" && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-bold text-stone-600">{g.type}</span>}
              {g.featured && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700"><Star className="h-3 w-3 fill-current" /> Mis en avant</span>}
              {!g.active && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-bold text-stone-500">Archivé</span>}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl" style={{ color }} data-testid="group-detail-name">{g.name}</h1>
            {g.description && <p className="mt-3 max-w-2xl text-stone-700">{g.description}</p>}

            {/* Ordre strict : 1. Responsable(s) La Mêlée, 2. Animateur(s). Masquer libellés vides. Pas de Référent CA */}
            {g.managers?.length > 0 && (
              <p className="mt-3 flex items-center gap-2 text-sm text-stone-700" data-testid="group-managers">
                <UserRound className="h-4 w-4 text-stone-400" />
                Responsable{g.managers.length > 1 ? "s" : ""} La Mêlée : <b>{g.managers.map((r) => r.name).join(", ")}</b>
              </p>
            )}
            {g.animators?.length > 0 && (
              <p className="mt-1 flex items-center gap-2 text-sm text-stone-700" data-testid="group-header-animators">
                <Users className="h-4 w-4 text-stone-400" />
                Animateur{g.animators.length > 1 ? "s" : ""} : <b>{g.animators.map((a) => a.full_name || a.name).join(", ")}</b>
              </p>
            )}
            {g.email && <p className="mt-1 flex items-center gap-2 text-sm text-stone-600"><Mail className="h-4 w-4" /> <a className="hover:underline" href={`mailto:${g.email}`}>{g.email}</a></p>}
            {g.parent && (
              <div className="mt-3 flex items-center gap-2 text-sm text-stone-600" data-testid="group-parent-chip">
                <span>Rattaché à :</span>
                <Link to={`${KINDS[g.parent.kind]?.path || "/commissions"}/${g.parent.id}`} className="transition-transform hover:scale-105">
                  <GroupChip group={g.parent} />
                </Link>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex flex-wrap gap-2">
              {isAdmin && <Button variant="outline" className="rounded-full bg-white" onClick={() => setEditing(true)} data-testid="group-edit-button"><Pencil className="h-4 w-4" /> Modifier</Button>}
              {isAuthed && g.notion_url && <Button asChild variant="outline" className="rounded-full bg-white" data-testid="group-open-notion-button"><a href={g.notion_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Notion</a></Button>}
              {isAdmin && g.active && <Button variant="outline" className="rounded-full border-red-200 bg-white text-red-600 hover:bg-red-50" onClick={() => setConfirm(true)} data-testid="group-delete-button"><Archive className="h-4 w-4" /> Archiver</Button>}
            </div>
            {isAdmin && (
              <label className="flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold">
                <Star className="h-4 w-4 text-amber-600" /> Mise en avant <Switch checked={g.featured} onCheckedChange={toggleFeatured} data-testid="group-featured-switch" />
              </label>
            )}
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="rounded-full px-5 font-semibold" style={{ backgroundColor: color }} data-testid="group-open-directory-button">
            <Link to={`/personnes?group=${id}`}>Voir les {plural(g.members_count, "membre", "membres")} dans le répertoire <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
      {g.children?.length > 0 && (
        <section className="mt-8" data-testid="group-children">
          <h2 className="mb-3 text-xl font-extrabold">Sous-groupes et groupes de travail rattachés</h2>
          <div className="flex flex-wrap gap-2.5">
            {g.children.map((c) => (
              <Link key={c.id} to={`${KINDS[c.kind]?.path || "/commissions"}/${c.id}`} className="transition-transform hover:scale-105" data-testid={`child-group-link-${c.id}`}>
                <GroupChip group={c} size="md" />
              </Link>
            ))}
          </div>
        </section>
      )}
      {(g.animators?.length > 0 || (kind === "commissions" && isAdmin)) && (
        <PeopleSection title="Animateurs" people={g.animators || []} testId="group-animators" canEdit={isAdmin}
          onAdd={(p) => animators_(p, "add")} onRemove={(p) => animators_(p, "remove")} empty="Aucun animateur pour le moment" />
      )}
      {members.isLoading ? <LoadingGrid count={3} /> : members.isError ? <ErrorState onRetry={members.refetch} /> : (
        <PeopleSection title={`Membres (${members.data.total})`} people={members.data.items} testId="group-members" canEdit={isAdmin}
          onAdd={(p) => members_(p, "add")} onRemove={(p) => members_(p, "remove")} empty="Aucun membre pour le moment" />
      )}
      {isAdmin && <GroupFormDialog kind={kind} group={g} open={editing} onOpenChange={setEditing} />}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} loading={deleting} onConfirm={archive} title={`Archiver « ${g.name} » ?`}
        description="Le Statut passe à « Archivé » dans Notion. Rien n'est supprimé, les membres sont conservés." />
    </div>
  );
}
